import api from "./api"
import { RegistrarLogErro } from "./clientefiel"

export interface PedidoViewItem {
    id: number
    idCliente: number
    email: string
    cliente: string
    facebookId: string | null
    telefone: string | null
    formaPagamento: string | null
    idFormaPagamento: number | null
    valorTotal: number
    valorDesconto: number | null
    taxaEntrega: number | null
    troco: number | null
    tipoPedido: string
    codigoStatus: number
    status: string
    statusPagamento: string
    plataforma: string
    enderecoIp: string | null
    latitude: number | null
    longitude: number | null
    motoboyPedido: string | null
    gateway: string | null
    urlFatura: string | null
    cupomFiscalEmitido: boolean
    entregaAgendada: string | null
    permiteCancelar: boolean
    linkPedido: string
    dataRealizacao: number
}

export interface ResumoFinanceiro {
    valorBruto: number
    descontos: number
    liquido: number
    percentualDesconto: number
    taxasEntrega: number
    qtdTotalEntregas: number
    perdidos: number
    qtdCancelados: number
}

export interface CartaoOnline {
    pagamentos: number
    taxa: number
    chargeback: number
    chargeBackPercentual: number
    saldo: number
}

export interface PixOnline {
    qtd: number
    valor: number
    taxas: number
    liquido: number
}

export interface FormaPagamentoResumo {
    nome: string
    valor: number
    qtd: number
}

export interface EntregadorResumo {
    nome: string
    vrTotal: number
    qtdEntregas: number
    vrDinheiro: number
    vrCartao: number
    vrTaxaEntrega: number
}

export interface ResultadoPedidos {
    pedidos: PedidoViewItem[]
    totalPedidos: number
    resumo: ResumoFinanceiro
    cartaoOnline: CartaoOnline
    pixOnline: PixOnline
    formasPagamento: FormaPagamentoResumo[]
    entregadores: EntregadorResumo[]
    fiscalAtivo: boolean
}

export interface FiltroPedidos {
    dataInicio: string
    dataFim: string
    horaInicio?: string
    horaFim?: string
    codigo?: string
    cupomDesconto?: string
    plataforma?: string
    idFormaPagamento?: string
    status?: string
    statusPagamento?: string
    emailCliente?: string
    nomeCliente?: string
    tipoPedido?: string
    tipoRecebimentoPedido?: string
    idMotoboy?: string
    idEstabelecimentoFilial?: string
    situacaoCupomFiscal?: string
    grupoFormaPagamento?: string
    exibirPedidosNaoConfirmados?: boolean
    exibirExpirados?: boolean
}

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

type RespostaRaw<T> = T & { codErro?: number; mensagem?: string }

function tratamentoResponse<T>(response: { data: RespostaRaw<T> }, chamada: string): T | ErroApi {
    const { data } = response
    if (data.codErro !== undefined && data.codErro > 0) {
        return { retornoErro: data.codErro, mensagem: data.mensagem ?? "" }
    }
    if (typeof data === "string" && data !== "") {
        return {
            retornoErro: 1,
            mensagem: `${chamada} - Erro inesperado, entre em contato com os responsáveis.`,
        }
    }
    return data
}

function erroParaRespostaRaw(error: unknown): RespostaRaw<never> {
    const axiosError = error as { message?: string; response?: { data?: unknown } }
    const data = axiosError.response?.data
    if (
        typeof data === "object" &&
        data !== null &&
        "codErro" in data &&
        (data as { codErro: number }).codErro > 0
    ) {
        const d = data as { codErro: number; mensagem?: string }
        return { codErro: d.codErro, mensagem: d.mensagem } as RespostaRaw<never>
    }
    if (axiosError.message === "Network Error") {
        return { codErro: 1, mensagem: "Verifique sua conexão!" } as RespostaRaw<never>
    }
    return { codErro: 1, mensagem: axiosError.message || "Erro desconhecido" } as RespostaRaw<never>
}

export const buscarPedidosFiltrados = async function (
    authorization: string,
    filtro: FiltroPedidos
): Promise<ResultadoPedidos | ErroApi> {
    try {
        const response = await api
            .post<RespostaRaw<ResultadoPedidos>>("pedido/ObterPedidosFiltrados", filtro, {
                headers: { "Content-Type": "application/json", Authorization: authorization },
            })
            .catch(function (error) {
                return { data: erroParaRespostaRaw(error) }
            })

        return tratamentoResponse<ResultadoPedidos>(response, "ObterPedidosFiltrados")
    } catch (error) {
        RegistrarLogErro(error, "ObterPedidosFiltrados", JSON.stringify(filtro))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

/** Coordenada de um pedido resolvida/persistida pelo backend. */
export interface CoordenadaPedido {
    id: number
    latitude: number
    longitude: number
}

/**
 * Geocodifica e PERSISTE (uma vez) as coordenadas dos endereços dos pedidos sem
 * latitude/longitude, via `pedido/preencher-coordenadas`. Usado pela Roteirização
 * para os pedidos de delivery cujo endereço nunca foi geocodificado — o detalhe
 * (`integracao/orders/{id}`) lê do mesmo Endereço, então sem isso eles não caem no
 * mapa. Retorna só os que ficaram com coordenada válida; falha/erro vira lista vazia
 * (o pedido apenas continua sem pino).
 */
export const preencherCoordenadasPedidos = async function (
    authorization: string,
    ids: number[]
): Promise<CoordenadaPedido[]> {
    if (!ids.length) return []
    try {
        const response = await api.post<CoordenadaPedido[] | { codErro?: number; mensagem?: string }>(
            "pedido/preencher-coordenadas",
            { ids },
            { headers: { "Content-Type": "application/json", Authorization: authorization } }
        )
        return Array.isArray(response.data) ? response.data : []
    } catch (error) {
        RegistrarLogErro(error, "preencherCoordenadasPedidos", JSON.stringify(ids))
        return []
    }
}
